import { useState } from 'react'
import { MessageSquare, Flag, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn, formatRelativeDate } from '@/lib/utils'
import {
  useNotes,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
} from '@/hooks/queries'
import type { ContentType } from '@/lib/types'
import { useAuth } from '@/contexts/AuthContext'

interface NotesPanelProps {
  entityType: ContentType
  entityId: string
}

const ENTITY_TYPE_PARAM: Record<ContentType, string> = {
  article: 'articleId',
  regulatory: 'regulatoryItemId',
  benchmark: 'benchmarkId',
  vulnerability: 'vulnerabilityId',
}

export function NotesPanel({
  entityType,
  entityId,
}: NotesPanelProps) {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const [open, setOpen] = useState(false)
  const [newText, setNewText] = useState('')

  const paramKey = ENTITY_TYPE_PARAM[entityType]
  const { data: notes = [], isLoading } = useNotes({ [paramKey]: entityId })

  const createNote = useCreateNote()
  const updateNote = useUpdateNote()
  const deleteNote = useDeleteNote()

  const flaggedCount = notes.filter((n) => n.isFlagged).length

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!newText.trim()) return
    await createNote.mutateAsync({
      contentType: entityType,
      contentId: entityId,
      authorId: userId,
      text: newText.trim(),
    })
    setNewText('')
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <MessageSquare className="h-4 w-4" />
          Notes
          {notes.length > 0 && (
            <span className="rounded-full bg-muted px-1.5 text-xs">{notes.length}</span>
          )}
          {flaggedCount > 0 && (
            <span className="rounded-full bg-orange-100 text-orange-700 px-1.5 text-xs dark:bg-orange-900/30 dark:text-orange-300">
              {flaggedCount} flagged
            </span>
          )}
          {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="mt-3 rounded-lg border bg-card p-4 space-y-4">
          {/* Add note form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-2">
            <Textarea
              placeholder="Add a note…"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              rows={2}
            />
            <Button
              type="submit"
              size="sm"
              disabled={!newText.trim() || createNote.isPending}
              className="self-end"
            >
              {createNote.isPending ? 'Saving…' : 'Add note'}
            </Button>
          </form>

          {/* Notes list */}
          {isLoading ? (
            <p className="text-xs text-muted-foreground">Loading notes…</p>
          ) : notes.length === 0 ? (
            <p className="text-xs text-muted-foreground">No notes yet.</p>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <div
                  key={note.id}
                  className={cn(
                    'flex gap-3 rounded-md p-3',
                    note.isFlagged
                      ? 'bg-orange-50 dark:bg-orange-900/10'
                      : 'bg-muted/50',
                  )}
                >
                  {/* Author avatar */}
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold uppercase">
                    {note.authorId.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium">{note.authorId}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatRelativeDate(note.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm">{note.text}</p>
                  </div>
                  <div className="flex items-start gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        'h-7 w-7',
                        note.isFlagged && 'text-orange-500',
                      )}
                      onClick={() =>
                        updateNote.mutate({ id: note.id, isFlagged: !note.isFlagged })
                      }
                      title={note.isFlagged ? 'Unflag' : 'Flag'}
                    >
                      <Flag className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => deleteNote.mutate(note.id)}
                      title="Delete note"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
